import React, { useState, useEffect } from "react";
import "./ContactForm.css";
import { Instagram, Linkedin, Mail, CheckCircle } from "lucide-react";
import {
  trackEvent,
  trackFormInteraction,
  trackException,
} from "./utils/analytics";

const ContactForm = ({ isModal = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });

  const [formState, setFormState] = useState("idle"); // idle, submitting, success
  const [isExiting, setIsExiting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Track form field interaction when user enters data
    trackFormInteraction("Contact Form", "Input Change", name);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // First set to submitting state for animation
    setFormState("submitting");

    // Track form submission attempt
    trackEvent("Form", "Submission Attempt", "Contact Form");

    // Create form data to submit to Netlify
    const formToSubmit = new FormData(e.target);

    // Submit the form data to Netlify
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(formToSubmit).toString(),
    })
      .then(() => {
        setFormState("success");

        // Track successful submission
        trackEvent("Form", "Submission Success", "Contact Form");

        // Reset form data
        setFormData({
          name: "",
          email: "",
          company: "",
          message: "",
        });

        // Start exit animation after 4 seconds
        setTimeout(() => {
          setIsExiting(true);
        }, 4000);

        // Reset form after exit animation completes
        setTimeout(() => {
          setIsExiting(false);
          setFormState("idle");
        }, 5500);
      })
      .catch((error) => {
        console.error("Form submission error:", error);
        alert(
          "Sorry, there was an error submitting the form. Please try again later."
        );
        setFormState("idle");

        // Track form submission error
        trackException(`Form submission error: ${error.message}`, false);
        trackEvent("Form", "Submission Error", "Contact Form");
      });
  };

  // Track social link clicks
  const handleSocialLinkClick = (platform) => {
    trackEvent("Social", "Click", platform);
  };

  return (
    <div className={`contact-form-container ${isModal ? "in-modal" : ""}`}>
      {/* Only show the heading when not in success state */}
      {formState !== "success" && <h2>Get in Touch</h2>}

      {/* Only show social links when not in success state */}
      {formState !== "success" && (
        <div className="form-social-links">
          <a
            href="mailto:eveyfayegriffiths@gmail.com"
            aria-label="Email me"
            data-interactive
            onClick={() => handleSocialLinkClick("Email")}
          >
            <Mail className="social-icon" size={24} />
          </a>
          <a
            href="https://www.instagram.com/eveylicious/"
            aria-label="Instagram"
            data-interactive
            onClick={() => handleSocialLinkClick("Instagram")}
          >
            <Instagram className="social-icon" size={24} />
          </a>
          <a
            href="https://www.linkedin.com/in/eveygriffiths/"
            aria-label="LinkedIn"
            data-interactive
            onClick={() => handleSocialLinkClick("LinkedIn")}
          >
            <Linkedin className="social-icon" size={24} />
          </a>
        </div>
      )}

      {/* Success message with improved animation */}
      {(formState === "success" || isExiting) && (
        <div
          className={`success-message-container ${isExiting ? "exiting" : ""}`}
        >
          <div className={`success-message ${isExiting ? "exiting" : ""}`}>
            <CheckCircle className="success-icon" size={40} />
            <h3>Thank you!</h3>
            <p>
              Your message has been sent successfully. I'll get back to you
              soon.
            </p>
          </div>
        </div>
      )}

      {/* Form with animation states and Netlify form attributes */}
      <form
        onSubmit={handleSubmit}
        className={`contact-form ${isModal ? "modal-form" : ""} ${
          formState === "submitting" ? "form-submitting" : ""
        } ${formState === "success" && !isExiting ? "form-success" : ""} ${
          isExiting ? "form-returning" : ""
        }`}
        name="contact"
        method="POST"
        data-netlify="true"
        netlify-honeypot="bot-field"
      >
        {/* Required hidden fields for Netlify forms */}
        <input type="hidden" name="form-name" value="contact" />
        <div className="hidden">
          <input name="bot-field" />
        </div>

        <div className={`form-fields ${isModal ? "modal-fields" : ""}`}>
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Your Name"
              className="contact-input"
              data-input="text"
              disabled={formState !== "idle"}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="your.email@example.com"
              className="contact-input"
              data-input="text"
              disabled={formState !== "idle"}
            />
          </div>

          <div className="form-group">
            <label htmlFor="company">Company</label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Your company (optional)"
              className="contact-input"
              data-input="text"
              disabled={formState !== "idle"}
            />
          </div>

          <div className="form-group message-group">
            <label htmlFor="message">Message *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              placeholder="Write your message here..."
              rows="6"
              className="contact-input"
              data-input="text"
              disabled={formState !== "idle"}
            />
          </div>
        </div>

        <button
          type="submit"
          className={`submit-button ${
            formState === "submitting" ? "submitting" : ""
          }`}
          data-interactive
          disabled={formState !== "idle"}
        >
          {formState === "idle"
            ? "Send Message"
            : formState === "submitting"
            ? "Sending..."
            : "Sent!"}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
